import type { OrderLine } from "~/@types/orderLines";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { MasterOrderInputs } from "~/store/masterOrderSlice";
import type { OrderInputs, StagedLineItem } from "~/store/orderSlice";
import type { AllocationItem } from "~fulfill/orders/columnDefs/allocationModalColDef";
import type { CreditOrderDraft } from "~fulfill/orders/helpers/creditOrderInputs";
import type { NewOrderAttempt } from "~fulfill/orders/helpers/newOrderAttempt";
import type { StagedShippedQuantities } from "~fulfill/orders/helpers/orderShipReview";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { MasterOrderCustomerDataInput, OrderCustomerDataInput } from "~/gql/graphqlBase.generated";
import isRecord from "~/helpers/isRecord";

export const ORDER_SOURCE_KIND = "order";

export const ORDERS_BASE_PATH = "/fulfill/orders";

export const ORDER_TARGET_KIND = "order";

const ORDER_ROUTE_PATTERN = /^\/fulfill\/orders\/order\/([^/?#]+)$/;

export const buildOrderPath = (orderId: string): string => `${ORDERS_BASE_PATH}/order/${encodeURIComponent(orderId)}`;

export const matchOrderReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = ORDER_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind: ORDER_TARGET_KIND };
	} catch {
		return null;
	}
};

export const orderReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildOrderPath(targetId),
	matchesRoute: matchOrderReceiverRoute
};

export interface TransferOrderDraft {
	customerId: string;
	name: string;
}

export type OrderDialogDraft =
	| { creditDraft: CreditOrderDraft }
	| { transferDraft: TransferOrderDraft; transferSourceSnapshot?: string }
	| {
			addLineItem?: StagedLineItem;
			fromLineItems?: boolean;
			stagedAllocations: Record<string, AllocationItem>;
			stagedShippedQuantities?: StagedShippedQuantities;
	  };

const isCreditOrderDraft = (value: unknown): value is CreditOrderDraft =>
	isRecord(value) &&
	(value.scope === "all" || value.scope === "lines") &&
	typeof value.reason === "string" &&
	Array.isArray(value.selections);

const isTransferOrderDraft = (value: unknown): value is TransferOrderDraft =>
	isRecord(value) && typeof value.customerId === "string" && value.customerId !== "" && typeof value.name === "string";

export const NEW_ORDER_EDITOR_ID = "new";

export interface NewOrderDraft {
	attempt: NewOrderAttempt | null;
	orderCustomer: OrderCustomerDataInput | null;
	orderInputs: OrderInputs;
	pickedCustomerId: null | string;
	searchText: string;
}

export interface OrderKitDraft {
	kitCount: number | null;
	lineItems: OrderLine[];
	orderCustomer?: OrderCustomerDataInput | null;
	orderInputs?: OrderInputs;
	savedParent: string;
	selectedKitId: string;
	siteId: string;
	typedKitCount: number | null;
}

export const orderKitSiteId = (
	order: { shipFromSiteLocationId?: string | null } | null | undefined,
	currentSiteId: string
): string => order?.shipFromSiteLocationId || currentSiteId;

export const isOrderKitSiteConflict = (
	parkedSiteId: string,
	order: { shipFromSiteLocationId?: string | null } | null | undefined,
	currentSiteId: string
): boolean => parkedSiteId !== orderKitSiteId(order, currentSiteId);

export const isOrderKitDraftStale = (
	draft: Pick<OrderKitDraft, "savedParent" | "siteId">,
	order: { shipFromSiteLocationId?: string | null } | null | undefined,
	currentSiteId: string
): boolean => isOrderKitSiteConflict(draft.siteId, order, currentSiteId) || draft.savedParent !== JSON.stringify(order);

export const isOrderKitDraft = (value: unknown): value is OrderKitDraft =>
	isRecord(value) &&
	(value.orderInputs === undefined || isRecord(value.orderInputs)) &&
	(value.orderCustomer === undefined || value.orderCustomer === null || isRecord(value.orderCustomer)) &&
	typeof value.selectedKitId === "string" &&
	typeof value.siteId === "string" &&
	(value.kitCount === null || typeof value.kitCount === "number") &&
	(value.typedKitCount === null || typeof value.typedKitCount === "number") &&
	Array.isArray(value.lineItems) &&
	typeof value.savedParent === "string";

export interface NewMasterOrderDraft {
	masterOrderCustomer: MasterOrderCustomerDataInput | null;
	masterOrderInputs: MasterOrderInputs;
	pickedCustomerId: null | string;
	searchText: string;
}

export type OrderSourceParking = OrderDialogDraft & {
	kitDraft?: OrderKitDraft;
	newMasterOrderDraft?: NewMasterOrderDraft;
	newOrderDraft?: NewOrderDraft;
	returnTo?: "sidePanel";
};

export type OrderSourceCheckpoint = OrderSourceParking & {
	orderId: string;
	orderNumber: string;
};

const isNullOr = (value: unknown, check: (present: unknown) => boolean): boolean => value === null || check(value);

export const isNewOrderDraft = (value: unknown): value is NewOrderDraft =>
	isRecord(value) &&
	isRecord(value.orderInputs) &&
	typeof value.searchText === "string" &&
	isNullOr(value.pickedCustomerId, (id) => typeof id === "string") &&
	isNullOr(value.orderCustomer, isRecord) &&
	isNullOr(value.attempt, isRecord);

export const isNewMasterOrderDraft = (value: unknown): value is NewMasterOrderDraft =>
	isRecord(value) &&
	isRecord(value.masterOrderInputs) &&
	typeof value.searchText === "string" &&
	isNullOr(value.pickedCustomerId, (id) => typeof id === "string") &&
	isNullOr(value.masterOrderCustomer, isRecord);

const isStagedShippedQuantities = (value: unknown): value is StagedShippedQuantities =>
	isRecord(value) && Object.values(value).every((quantity) => typeof quantity === "number");

export const isOrderSourceCheckpoint = (value: unknown): value is OrderSourceCheckpoint =>
	isRecord(value) &&
	typeof value.orderId === "string" &&
	typeof value.orderNumber === "string" &&
	(isRecord(value.stagedAllocations) ||
		isCreditOrderDraft(value.creditDraft) ||
		isTransferOrderDraft(value.transferDraft)) &&
	(value.kitDraft === undefined || isOrderKitDraft(value.kitDraft)) &&
	(value.newOrderDraft === undefined || isNewOrderDraft(value.newOrderDraft)) &&
	(value.transferSourceSnapshot === undefined || typeof value.transferSourceSnapshot === "string") &&
	(value.newMasterOrderDraft === undefined || isNewMasterOrderDraft(value.newMasterOrderDraft)) &&
	(value.returnTo === undefined || value.returnTo === "sidePanel") &&
	(value.fromLineItems === undefined || typeof value.fromLineItems === "boolean") &&
	(value.addLineItem === undefined || isRecord(value.addLineItem)) &&
	(value.stagedShippedQuantities === undefined || isStagedShippedQuantities(value.stagedShippedQuantities));

const orderEditorKey = (orderId: string): string => `order:${orderId || NEW_ORDER_EDITOR_ID}`;

export default orderEditorKey;
